import { useUpdatePageStatusMutation } from 'api/collaborateApi.ts';
import { useAddPageMutation, useGetPagesQuery } from 'api/siteApi.ts';
import {
  CircleCheck,
  CircleDot,
  CircleDotDashed,
  CircleEllipsis,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, DropdownMenu, Skeleton } from 'ui';
import { cn } from 'utils/cn';
import { normalizeUrl } from '../../../../../utils/proxyUtils.ts';
import { usePermissions } from '../../../hooks/use-permissions.ts';
import { getPageId } from '../../../store/tasksSlice.ts';
import { getIsBottomBarLoading } from '../../../store/uiSlice.ts';

interface Props {
  siteId?: string | null;
}

type PageStatusType = 'in-progress' | 'needs-review' | 'approved' | null

// const lockTitle = "Status & Priority"
// const lockDescription = "Update task progress and set urgency with a click — keep your team aligned and projects moving forward."
// const lockModalImgSetting = 'https://wpfeedback-image.s3.us-east-2.amazonaws.com/images/integration-lock-images/lock-user-image.jpg'

export const PageStatus = ({ siteId }: Props) => {
  const { t } = useTranslation();
  const { isPermitted } = usePermissions();
  // const dispatch = useDispatch();
  // const user = useSelector(getUser);
  const pageSavedId = useSelector(getPageId)
  const { data } = useGetPagesQuery({ siteId: siteId ?? '' }, { skip: !siteId })
  const [addPage, { data: addPageResult }] = useAddPageMutation()
  const [buttonLabel, setButtonLabel] = useState('')
  const [update] = useUpdatePageStatusMutation()
  const [pageId, setPageId] = useState<string | null>(null)
  const [pageStatus, setPageStatus] = useState<PageStatusType | null>(null)
  const isBottomBarLoading = useSelector(getIsBottomBarLoading);
  useEffect(() => {
    if (!data) return

    const currentUrl = normalizeUrl(window.location.href)

    const matchingPage = data.data.pages.page?.find(p => {
      try {
        // Use the same logic as page-card component
        // Use the correct property for the page URL depending on whether we're running inside the Atarim app (window.ATARIM is true)
        // If inside Atarim, use p.url; otherwise, use p.target_url. This ensures URL normalization works for both environments.
        const pageUrl = normalizeUrl(window.ATARIM ? p.url : p.target_url);
        return pageUrl === currentUrl;
      } catch (error) {
        console.error('Error comparing URLs:', error);
        return false;
      }
    })

    if (matchingPage) {
      setPageId(matchingPage.id)
      setPageStatus(matchingPage.status)
    } else if (pageSavedId) {
      // If no matching page found but we have a saved pageId, keep it
      setPageId(pageSavedId)
      // Don't set pageStatus here - let it come from API when available
    } else {
      setPageId(null)
      setPageStatus(null)
    }
  }, [data, pageSavedId])

  useEffect(() => {
    if (!addPageResult) return
    setPageId(addPageResult.result.id)
    setPageStatus(addPageResult.result.status)
  }, [addPageResult])

  const defaultLabel = t('bottom_bar.page_status.title', 'Page Status');

  // const locked = !user?.raw?.plan?.approve_design_enabled;

  const handleSelect = (value: PageStatusType, label: string) => {
    if (!siteId) return;
    // if (value === 'approved') {
    //   if (locked) {
    //     dispatch(setShowLockFeature({
    //       showLockFeature: true,
    //       lockTitle: lockTitle,
    //       lockParagraph: lockDescription,
    //       lockImg: lockModalImgSetting,
    //       rank: 2,
    //     }));
    //     return;
    //   }
    // }
    setButtonLabel(label)

    if (pageId) {
      update({
        site_id: siteId,
        page_id: pageId,
        status: value,
        set_tasks_complete: false,
        is_godaddy_site: false,
      })
    } else {
      addPage({
        page_url: normalizeUrl(window.location.href),
        site_id: siteId,
        status: value
      })
    }
  };

  const StateIcon = ({ size }: { size: number }) => {
    if (pageStatus === null) return <CircleDot size={size} />;
    if (pageStatus === 'in-progress') return <CircleDotDashed size={size} />;
    if (pageStatus === 'needs-review') return <CircleEllipsis size={size} />;
    if (pageStatus === 'approved') return <CircleCheck size={size} />;
    return null;
  }

  useEffect(() => {
    if (pageStatus === null) setButtonLabel('')
    if (pageStatus === 'in-progress') setButtonLabel('In Progress')
    if (pageStatus === 'needs-review') setButtonLabel('Needs Review')
    if (pageStatus === 'approved') setButtonLabel('Approved')
  }, [pageStatus])

  return (
    <>
      {isBottomBarLoading ? (
        <Skeleton className="h-9 w-32 rounded-md" />
      ) : (
        <>
          <DropdownMenu modal={false}>
            <DropdownMenu.Trigger
              className={cn(
                'hidden md:flex border rounded-lg',
                pageStatus === 'in-progress' && 'border-ata-orange',
                pageStatus === 'needs-review' && 'border-ata-gold',
                pageStatus === 'approved' && 'bg-primary hover:bg-primary',
              )}
              asChild
            >
              <Button variant="outline" size="sm" leftIcon={<StateIcon size={18} />} >
                {buttonLabel === '' ? defaultLabel : buttonLabel}
              </Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Content className="z-10" portal={false}>
              {isPermitted("collaborate-bottom-bar-page-status-no-status") && (
                <DropdownMenu.Item
                  className="focus:bg-secondary cursor-pointer focus:text-white"
                  onClick={() => handleSelect(null, 'No Status')}
                >
                  <div className="flex items-center gap-1">
                    <CircleDot size={18} />
                    {t('bottom_bar.page_status.no_status', 'No Status')}
                  </div>
                </DropdownMenu.Item>
              )}

              {isPermitted("collaborate-bottom-bar-page-status-in-progress") && (
                <DropdownMenu.Item
                  className="focus:bg-ata-orange focus:text-ata-grey cursor-pointer"
                  onClick={() => handleSelect('in-progress', 'In Progress')}
                >
                  <div className="flex items-center gap-1">
                    <CircleDotDashed size={18} />
                    {t('bottom_bar.page_status.in_progress', 'In Progress')}
                  </div>
                </DropdownMenu.Item>
              )}


              {isPermitted("collaborate-bottom-bar-page-status-needs-review") && (
                <DropdownMenu.Item
                  className="focus:bg-ata-gold focus:text-ata-grey cursor-pointer"
                  onClick={() => handleSelect('needs-review', 'Needs Review')}
                >
                  <div className="flex items-center gap-1">
                    <CircleEllipsis size={18} />
                    {t('bottom_bar.page_status.needs_review', 'Needs Review')}
                  </div>
                </DropdownMenu.Item>
              )}

              {isPermitted("collaborate-bottom-bar-page-status-approved") && (
                <DropdownMenu.Item
                  className="focus:bg-ata-green focus:text-ata-grey cursor-pointer"
                  onClick={() => handleSelect('approved', 'Approved')}
                >
                  <div className="flex items-center gap-1">
                    <CircleCheck size={18} />
                    {t('bottom_bar.page_status.approved', 'Approved')}
                  </div>
                </DropdownMenu.Item>
              )}
            </DropdownMenu.Content>
          </DropdownMenu>
        </>
      )}
    </>
  );

};

export default PageStatus;